import React, { useState } from 'react'
import { Link } from 'gatsby'
import FlyoutMenu, { FlyoutMenuItem } from './FlyoutMenu'
import { HUBSPOT_SCHEDULE_A_TOUR_URL } from '../leadFlowModal/constants'
import { RENTAL_APPLICATION_URLS } from '../rentalApplication/constants'
import '@scss/hamburgers-white-theme.scss'

// -------------------------
// MenuButton Component
// -------------------------
export interface MenuButtonProps {
  isMenuOpen?: boolean
  toggleMenu: () => void
}

export interface NavbarProps {
  isHero?: boolean
  menuContentOnly?: boolean
}

export const MenuButton: React.FC<MenuButtonProps> = ({
  isMenuOpen = false,
  toggleMenu,
}) => (
  <button
    aria-controls="navigation"
    aria-label="Menu"
    className={`hamburger hamburger--slider ${isMenuOpen ? 'is-active' : ''} block mr-2 border-none md:hidden`}
    onClick={toggleMenu}
    type="button"
  >
    <span className="hamburger-box md:hidden">
      <span className="hamburger-inner"></span>
    </span>
  </button>
)

interface NavItem {
  name: string;
  href?: string;
  subItems?: { name: string; href: string }[];
}

// Define the nav items with (optional) subItems.
const navItems: NavItem[] = [
  {
    name: 'Tempe',
    subItems: [
      { name: 'Available Floor Plans', href: RENTAL_APPLICATION_URLS.UNIT_SELECTION_URL },
      { name: 'Schedule a tour', href: HUBSPOT_SCHEDULE_A_TOUR_URL },
      { name: 'Neighborhood', href: '/tempe' },
      { name: 'Events', href: '/tempe/events' },
      { name: 'Retail', href: '/tempe/retail' },
      { name: 'Map', href: '/tempe/map' },
      { name: 'Photo Gallery', href: 'https://prospectportal.culdesac.com/Apartments/module/photos/property%5Bid%5D/1279098/' },
    ],
  },
  {
    name: 'Company',
    subItems: [
      { name: '1070 Dill Ave Townhomes', href: 'https://1070dill.com' },
      { name: '1121 Allene Ave Townhomes', href: 'https://1121Allene.com' },
      { name: 'About Us', href: 'https://culdesac.com/about' },
      { name: 'Merch', href: 'https://shop.culdesac.com' },
      { name: 'Careers', href: '/careers' },
    ],
  },
]

const Navbar: React.FC<NavbarProps> = ({ isHero = false, menuContentOnly = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  // Track which section is open in the mobile menu
  const [openSection, setOpenSection] = useState<string | null>(null)

  // Render mobile menu content
  const renderMobileMenuContent = () => (
    <ol className="md:hidden flex flex-col uppercase tracking-widest font-medium gap-4">
      {navItems.map((item) =>
        item.subItems ? (
          <li key={item.name} className="w-full">
            <button
              onClick={() =>
                setOpenSection(openSection === item.name ? null : item.name)
              }
              className="flex items-center w-full py-2 pl-4 text-left uppercase tracking-widest font-medium"
            >
              <span className="flex-grow">{item.name}</span>
              <span className="ml-auto">
                {openSection === item.name ? '▼\uFE0E' : '▶\uFE0E'}
              </span>
            </button>
            {openSection === item.name && (
              <ol className="px-2 mt-1">
                {item.subItems.map((sub) => (
                  <li key={sub.name} className="cursor-pointer">
                    <Link
                      to={sub.href}
                      className="block py-2 pl-4 hover:bg-gray-50 transition duration-150 ease-in-out uppercase tracking-widest font-medium text-new-white"
                    >
                      {sub.name}
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </li>
        ) : (
          item.href && (
            <li key={item.name} className="cursor-pointer">
              <Link
                to={item.href}
                className="block py-2 pl-4 hover:bg-gray-50 transition duration-150 ease-in-out uppercase tracking-widest font-medium text-new-white"
              >
                {item.name}
              </Link>
            </li>
          )
        )
      )}
    </ol>
  )

  return (
    <nav className="w-full flex-1">
      {/* DESKTOP NAV: visible on md and up */}
      {!menuContentOnly && (
        <ol className="hidden md:flex gap-10 uppercase tracking-widest font-medium">
          {navItems.map((item) =>
            item.subItems ? (
              <li
                key={item.name}
                className="relative group inline-flex justify-center items-center cursor-pointer"
              >
                <FlyoutMenu menuTitle={item.name} isHero={isHero}>
                  {item.subItems.map((sub) => (
                    <li key={sub.name}>
                      <FlyoutMenuItem href={sub.href} name={sub.name} />
                    </li>
                  ))}
                </FlyoutMenu>
              </li>
            ) : (
              item.href && (
                <li
                  key={item.name}
                  className="relative group inline-flex justify-center items-center cursor-pointer"
                >
                  <Link to={item.href}>
                    {isHero && <span className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-0 h-0 rounded-full bg-new-teal transition-all duration-300 ease-out group-hover:w-16 group-hover:h-16" />}
                    <span className="relative z-10 transition-transform duration-300 ease-out group-hover:scale-110">
                      {item.name}
                    </span>
                  </Link>
                </li>
              )
            )
          )}
        </ol>
      )}

      {/* MOBILE NAV */}
      {!menuContentOnly ? (
        // When not menuContentOnly:
        // 1. Show hamburger button 
        // 2. Show menu content when isOpen
        <>
          <div className="block md:hidden w-full pl-3 xs:pl-[0.9rem] hamburger-theme-white">
            <MenuButton isMenuOpen={isOpen} toggleMenu={() => setIsOpen(!isOpen)} />
          </div>
          {isOpen && <div className="mt-4">{renderMobileMenuContent()}</div>}
        </>
      ) : (
        // When menuContentOnly is true:
        // Just render the menu content without hamburger
        renderMobileMenuContent()
      )}
    </nav>
  )
}

export default Navbar
